// PRICING_REDESIGN_SUBSCRIPTION_POPUP_FULL_FILE

// Import React elements
import { useContext, useMemo, useState } from "react";

// Import strich api calls
import { addUpdateAccountSubscription } from "api/APIStrichCalls";

// Import context function
import { AuthContext } from "global/context/AuthContext";

// Import hooks
import { useToast } from "global/hooks/useToast";

// Import components
import CreateButton from "global/components/CreateButton";

// Import casing utility
import { ToTitleCase } from "global/utilities/ConvertCasing";

// Import currency utility
import { FormatCurrency } from "global/utilities/ConvertCurrency";

// Import utilities
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";



// This exports the popup component
export default function SubscriptionOptionsPopup({
    activeSubscription,
    onClose,
    subscriptionLevels,
    canUpgrade,
    currentSubscriptionLevelIsFree,
    userData,
    setIsUpgradePopupOpen,
    setLoading,
    refreshUserData,
    defaultPlanToSelect,
}) {

    // Get the authentication token from the AuthContext
    const { token } = useContext(AuthContext);

    // Define toast options
    const { toastSuccess, toastError } = useToast();

    // This safely gets the available upgrades array
    const availableUpgrades = useMemo(() => {

        return activeSubscription?.available_upgrades || [];

    }, [activeSubscription?.available_upgrades]);

    // This safely gets the current subscription level
    const currentLevel = activeSubscription?.subscription_level || "current";

    // This safely gets the current package
    const currentPackage = activeSubscription?.subscription_package || "";

    // This gets the default package code from the selected plan if passed from the pricing card
    const defaultUpgradeCode = defaultPlanToSelect?.selectedPackageCode || defaultPlanToSelect?.packages?.yearly?.code || defaultPlanToSelect?.packages?.monthly?.code || "";

    // This stores the currently selected upgrade code
    const [selectedUpgradeCode, setSelectedUpgradeCode] = useState(defaultUpgradeCode);

    // This finds the selected upgrade object
    const selectedUpgrade = useMemo(() => {

        // Return the matching upgrade option
        return availableUpgrades.find((upgradeOption) => upgradeOption.code === selectedUpgradeCode);

    }, [availableUpgrades, selectedUpgradeCode]);

    // This finds the selected subscription level
    const selectedSubscriptionLevel = useMemo(() => {

        // Return nothing when no selected upgrade exists
        if (!selectedUpgrade) {

            // Return null fallback
            return null;

        }

        // Return the matching level
        return subscriptionLevels?.find((level) => level.code === selectedUpgrade.subscription_level) || null;

    }, [selectedUpgrade, subscriptionLevels]);

    // This formats prices using the account currency
    const formatCurrency = (value) => {

        // Return the value formatted using the account currency
        return FormatCurrency(userData?.currency, value || 0);

    };

    // This turns duration into readable text
    const getDurationLabel = (months) => {

        // Return monthly text for one month
        if (Number(months) === 1) {

            // Return monthly label
            return "Monthly";

        }

        // Return yearly text for twelve months
        if (Number(months) === 12) {

            // Return yearly label
            return "Yearly";

        }

        // Return a fallback for other durations
        return `${months} months`;

    };

    // This turns duration into renewal text
    const getRenewalLabel = (months) => {

        // Return monthly renewal text
        if (Number(months) === 1) {

            // Return monthly renewal label
            return "Renews monthly";

        }

        // Return yearly renewal text
        if (Number(months) === 12) {

            // Return yearly renewal label
            return "Renews yearly";

        }

        // Return fallback renewal label
        return `Renews every ${months} months`;

    };

    // This gets a display value from subscription level settings
    const getSettingDisplayValue = (level, settingKey) => {

        // Get settings safely
        const settings = level?.settings || {};

        // Handle queued AI image limit
        if (settingKey === "ai_concurrent_limit") {

            // Return unlimited for the API's large value
            return String(settings?.ai_concurrent_limit) === "999999" ? "Unlimited" : settings?.ai_concurrent_limit || "—";

        }

        // Handle queue priority
        if (settingKey === "ai_queue_priority") {

            // Return setting value
            return settings?.ai_queue_priority || "—";

        }

        // Handle retention days
        if (settingKey === "ai_retention_days") {

            // Return setting value with days
            return settings?.ai_retention_days ? `${settings.ai_retention_days} days` : "—";

        }

        // Handle platform fee
        if (settingKey === "store_platform_fee") {

            // Return percentage value
            return settings?.store_platform_fee !== undefined ? `${Number(settings.store_platform_fee) * 100}%` : "—";

        }

        // Handle payment fee
        if (settingKey === "store_payment_fee") {

            // Return percentage value
            return settings?.store_payment_fee !== undefined ? `${Number(settings.store_payment_fee) * 100}%` : "—";

        }

        // Return fallback value
        return "—";

    };

    // This gets the current subscription level object
    const currentSubscriptionLevel = useMemo(() => {

        // Return the matching current level
        return subscriptionLevels?.find((level) => level.code === currentLevel) || null;

    }, [subscriptionLevels, currentLevel]);

    // This builds the feature changes shown in the upgrade summary
    const featureChanges = useMemo(() => {

        // Define stable comparison fields for CI-friendly diffs
        const rows = [
            {
                label: "Queued AI images",
                key: "ai_concurrent_limit",
            },
            {
                label: "Image retention",
                key: "ai_retention_days",
            },
            {
                label: "Platform fee",
                key: "store_platform_fee",
            },
            {
                label: "Payment fee",
                key: "store_payment_fee",
            },
        ];

        // Map fields into display rows
        return rows.map((row) => {

            // Return one feature change row
            return {
                label: row.label,
                before: getSettingDisplayValue(currentSubscriptionLevel, row.key),
                after: getSettingDisplayValue(selectedSubscriptionLevel, row.key),
            };

        });

    }, [currentSubscriptionLevel, selectedSubscriptionLevel]);

    // This groups available upgrade options by subscription level
    const upgradeGroups = useMemo(() => {

        // Create an empty object for grouped upgrades
        const groupedUpgrades = {};

        // Loop through each upgrade option
        availableUpgrades.forEach((upgradeOption) => {

            // Create the group if needed
            if (!groupedUpgrades[upgradeOption.subscription_level]) {

                // Store the group
                groupedUpgrades[upgradeOption.subscription_level] = [];

            }

            // Add the upgrade option to the matching group
            groupedUpgrades[upgradeOption.subscription_level].push(upgradeOption);

        });

        // Return grouped upgrade options
        return groupedUpgrades;

    }, [availableUpgrades]);

    // This handles clicking an upgrade card
    const handleSelectCard = (upgradeCode) => {

        // Store the selected upgrade code
        setSelectedUpgradeCode(upgradeCode);

    };

    // This handles confirming the selected upgrade
    const handleConfirmSelection = () => {

        // Stop the function if no upgrade is selected
        if (!selectedUpgrade) {

            // Return early
            return;

        }

        // Send the selected upgrade to the upgrade function
        handleUpgradeSubscription(selectedUpgrade, currentSubscriptionLevelIsFree);

    };

    // Function to handle upgrading the plan
    const handleUpgradeSubscription = async (selectedUpgradeOption, addNewSubscription = false) => {

        // Return early if we do not have a current subscription id
        if (!addNewSubscription && !userData?.active_subscription?.id) {

            // Stop here
            return;

        }

        // Display confirmation
        DisplayConfirmationDialog(

            `<h3>Confirm upgrade to ${selectedUpgradeOption.name}</h3>
            <br/>
            <span class="subscription__cost-message">
                You will be charged <b>${FormatCurrency(userData.currency, selectedUpgradeOption.upgrade_price_inc_tax)}</b> today.
            </span>
            <br/>
            <span class="subscription__cost-submessage">
                This includes ${FormatCurrency(userData.currency, selectedUpgradeOption.upgrade_tax)} tax and will be taken from your linked billing account.
            </span>
            <br/>
            <br/>
            <span class="subscription__cost-submessage">
                Auto-renew will be enabled so your subscription stays active.
            </span>
            <br/>
            <span class="subscription__cost-message">
                Renewal price is <b>${FormatCurrency(userData.currency, selectedUpgradeOption.price_inc_tax)}</b>.
            </span>
            <br/>
            <span class="subscription__cost-submessage">
                You can cancel anytime by turning off auto-renew in your account settings.
            </span>
            <br/>`,
            "Upgrade",
            "Cancel",

            async function (isConfirmed) {

                // If user confirms, proceed with upgrade
                if (isConfirmed) {

                    // Set loading state
                    setLoading(true);

                    // Create payload
                    const payload = addNewSubscription ? {
                        subscription_package: selectedUpgradeOption.code,
                        start_mode: "immediate",
                        auto_renew: "Y",
                    } : {
                        subscription_package: selectedUpgradeOption.code,
                        start_mode: "immediate",
                        upgrade_from: userData.active_subscription.id,
                    };

                    // Send request to API
                    const response = await addUpdateAccountSubscription(token, payload);

                    // Handle successful response
                    if (response.success) {

                        // Refresh user data when available
                        if (refreshUserData) {

                            // Make the call to update the UI with the new subscription
                            await refreshUserData();

                        }

                        // Close the popup
                        setIsUpgradePopupOpen(false);

                        // Clear loading state
                        setLoading(false);

                        // Show success toast
                        toastSuccess({ title: "Success", message: "Subscription upgraded." });

                    } else {

                        // Clear loading state
                        setLoading(false);

                        // Show error toast
                        toastError({ title: "Error", message: "Could not upgrade subscription: " + response.error_message, autoCloseMs: 6000 });

                    }

                }

            }

        );

    };

    // This renders the upgrade popup
    return (

        <div className="popup-overlay">

            <div className="popup-container popup-container--large upgrade-options-popup__modal">

                <div className="popup__header upgrade-options-popup__header">

                    <div className="popup__headerLeft">

                        <h2 className="popup__title upgrade-options-popup__title">

                            Upgrade from {ToTitleCase(currentLevel)} {currentPackage.includes("_monthly") ? "(Monthly)" : currentPackage.includes("_yearly") ? "(Yearly)" : ""}

                        </h2>

                        <p className="popup__subtitle upgrade-options-popup__subtitle">

                            {canUpgrade ? "Review your upgrade, compare package options, then confirm when ready." : "Link your Stripe billing account to upgrade your subscription plan."}

                        </p>

                    </div>

                    <div className="popup__headerRight">

                        <button
                            type="button"
                            className="popup__close upgrade-options-popup__close"
                            onClick={() => onClose?.()}
                            title="Close"
                        >

                            🞬

                        </button>

                    </div>

                </div>

                <div className="popup-content popup-content__body upgrade-options-popup__body">

                    <div className="upgrade-options-popup__checkout-layout">

                        <section className="upgrade-options-popup__summary-panel">

                            <div className="upgrade-options-popup__summary-header">

                                <span className={`pod-pricing-page__plan-dot pod-pricing-page__plan-dot--${selectedUpgrade?.subscription_level || "default"}`} />

                                <div>

                                    <h3 className="upgrade-options-popup__summary-title">

                                        {selectedUpgrade ? `Upgrade to ${selectedUpgrade.name}` : "Choose an upgrade"}

                                    </h3>

                                    <p className="upgrade-options-popup__summary-subtitle">

                                        {selectedUpgrade ? `${getDurationLabel(selectedUpgrade.duration_in_months)} package selected` : "Select a package to see the upgrade summary."}

                                    </p>

                                </div>

                            </div>

                            {selectedUpgrade ? (

                                <>

                                    <div className="upgrade-options-popup__price-summary-card">

                                        <div>

                                            <span className="upgrade-options-popup__price-label">

                                                You will be charged today

                                            </span>

                                            <strong className="upgrade-options-popup__summary-price">

                                                {formatCurrency(selectedUpgrade.upgrade_price_inc_tax)}

                                            </strong>

                                            <span className="upgrade-options-popup__tax-note upgrade-options-popup__tax-note--strong">

                                                Includes {formatCurrency(selectedUpgrade.upgrade_tax)} tax

                                            </span>

                                        </div>

                                        <div className="upgrade-options-popup__renewal-box">

                                            <span>

                                                {getRenewalLabel(selectedUpgrade.duration_in_months)}

                                            </span>

                                            <strong>

                                                {formatCurrency(selectedUpgrade.price_inc_tax)}

                                            </strong>

                                        </div>

                                    </div>

                                    <div className="upgrade-options-popup__info-grid">

                                        <div className="upgrade-options-popup__info-card">

                                            <span className="upgrade-options-popup__info-label">

                                                Billing

                                            </span>

                                            <p>

                                                Payment will be taken from your linked billing account.

                                            </p>

                                        </div>

                                        <div className="upgrade-options-popup__info-card">

                                            <span className="upgrade-options-popup__info-label">

                                                Auto-renew

                                            </span>

                                            <p>

                                                Auto-renew is enabled. You can cancel anytime.

                                            </p>

                                        </div>

                                    </div>

                                    <div className="upgrade-options-popup__changes-panel">

                                        <h4 className="upgrade-options-popup__small-title">

                                            What changes

                                        </h4>
                                        
                                        <table className="upgrade-options-popup__changes-table">

                                            <thead>

                                                <tr>

                                                    <th>

                                                        Feature

                                                    </th>

                                                    <th className="upgrade-options-popup__changes-table--value">

                                                        Current

                                                    </th>

                                                    <th className="upgrade-options-popup__changes-table--value">

                                                        After upgrade

                                                    </th>

                                                </tr>

                                            </thead>

                                            <tbody>

                                                {featureChanges.map((change) => (

                                                    <tr key={change.label}>

                                                        <td>

                                                            {change.label}

                                                        </td>

                                                        <td className="upgrade-options-popup__changes-table--value">

                                                            {change.before}

                                                        </td>

                                                        <td className="upgrade-options-popup__changes-table--value">

                                                            <strong>

                                                                {change.after}

                                                            </strong>

                                                        </td>

                                                    </tr>

                                                ))}

                                            </tbody>

                                        </table>

                                    </div>

                                </>

                            ) : (

                                <div className="upgrade-options-popup__empty-summary">

                                    Select a package to continue.

                                </div>

                            )}

                        </section>

                        <section className="upgrade-options-popup__options-panel">

                            <div className="upgrade-options-popup__options-heading">

                                <h3 className="upgrade-options-popup__small-title">

                                    Change package

                                </h3>

                                <p>

                                    Monthly gives flexibility. Yearly usually gives the best long-term value.

                                </p>

                            </div>

                            <div className="upgrade-options-popup__group-list">

                                {subscriptionLevels?.map((level) => {

                                    // Get upgrades for this level
                                    const levelUpgrades = upgradeGroups[level.code] || [];

                                    // Hide levels with no available upgrades
                                    if (levelUpgrades.length === 0) {

                                        // Return nothing
                                        return null;

                                    }

                                    // Return one group
                                    return (

                                        <div
                                            key={level.code}
                                            className="upgrade-options-popup__level-group"
                                        >

                                            <div className="upgrade-options-popup__level-heading">

                                                <div className="pod-pricing-page__plan-name-wrap">

                                                    <span className={`pod-pricing-page__plan-dot pod-pricing-page__plan-dot--${level.code}`} />

                                                    <h4 className="upgrade-options-popup__level-title">

                                                        {ToTitleCase(level.code)}

                                                    </h4>

                                                </div>

                                                <span className="upgrade-options-popup__level-description">

                                                    {level.description}

                                                </span>

                                            </div>

                                            <div className="upgrade-options-popup__package-list">

                                                {levelUpgrades.map((upgradeOption) => {

                                                    // Check whether this card is selected
                                                    const isSelected = selectedUpgradeCode === upgradeOption.code;

                                                    // Check whether this option is yearly
                                                    const isYearly = Number(upgradeOption.duration_in_months) === 12;

                                                    // Render one package option
                                                    return (

                                                        <button
                                                            className={`upgrade-options-popup__package-card ${isSelected ? "upgrade-options-popup__package-card--selected" : ""}`}
                                                            key={upgradeOption.code}
                                                            type="button"
                                                            onClick={() => handleSelectCard(upgradeOption.code)}
                                                        >

                                                            <div className="upgrade-options-popup__package-main">

                                                                <span className={`upgrade-options-popup__duration-pill ${isYearly ? "upgrade-options-popup__duration-pill--yearly" : ""}`}>

                                                                    {getDurationLabel(upgradeOption.duration_in_months)}

                                                                </span>

                                                                <strong>

                                                                    {formatCurrency(upgradeOption.upgrade_price_inc_tax)}

                                                                </strong>

                                                                <span>

                                                                    Includes {formatCurrency(upgradeOption.upgrade_tax)} tax

                                                                </span>

                                                            </div>

                                                            <div className="upgrade-options-popup__package-side">

                                                                <span>

                                                                    Renewal

                                                                </span>

                                                                <strong>

                                                                    {formatCurrency(upgradeOption.price_inc_tax)}

                                                                </strong>

                                                            </div>

                                                        </button>

                                                    );

                                                })}

                                            </div>

                                        </div>

                                    );

                                })}

                            </div>

                        </section>

                    </div>

                </div>

                <div className="tabs-footer tabs-footer--fill-no-background upgrade-options-popup__footer">

                    <div className="upgrade-options-popup__footer-note">

                        {selectedUpgradeCode && !canUpgrade && (

                            <span className="warning-message">

                                You need to link a payment method before you can upgrade to this plan.

                            </span>

                        )}

                    </div>

                    <div className="horizontal-align-content-center">

                        <CreateButton
                            type="button"
                            title="Confirm upgrade"
                            size="m"
                            onClick={handleConfirmSelection}
                            disabled={!selectedUpgradeCode || !canUpgrade}
                        />

                        <CreateButton
                            type="button"
                            title="Cancel"
                            isCancel={true}
                            invert={true}
                            size="m"
                            onClick={() => onClose()}
                        />

                    </div>

                </div>

            </div>

        </div>

    );

}